import {
  type CountryRegionalMapData,
  type Top5LocationData,
  ResponseClickStreams,
  ResponseDropDownAnalysis,
  ResponseRegionalMapData,
  ResponseUserBehaviourAnalysis,
  ResponseUserBehaviourHeatmap,
  ResponseUserBehaviourHoverEngagement,
  ResponseUserBehaviourTimeOnPage,
  ResponseUserBehaviourScrollDepth,
  ResponseDropOffByTrafficType,
  ResponseDropOffByVisitorType
} from '@/lib/services'
import { LandingPage } from '@prisma/client'
import { Chart } from 'chart.js'
import { ApexOptions } from 'apexcharts'
import { JsonValue } from '@prisma/client/runtime/library'

export interface LandingSubPageData {
  pageId: string
  pageName: string | null
  pageDescription: string | null
  status: boolean
  contentJson: JsonValue
  createdAt: Date
  updatedAt: Date
  landingPageId: string
  siteHsrId: number
  funnelId: number | null
}

export interface ExtendedLandingPage extends LandingPage {
  Page?: Array<{
    pageId: string
    pageName: string
    pageDescription: string
    landingPageId: string
    siteHsrId: number
    funnelId: number
    status: boolean
    aiEnhanced: boolean
  }>
  subAccountId?: string
  siteId: number
  landingPageId: string
  name: string
}
export interface funnelObj {
  pageId: string
  pageName: string
  pageDescription: string
  landingPageId: string
  siteHsrId: number
  funnelId: number | null
  status: boolean
  aiEnhanced: boolean
}
export const defaultFunnelObj: funnelObj = {
  pageId: '',
  pageName: '',
  pageDescription: '',
  landingPageId: '',
  siteHsrId: -1,
  funnelId: -1,
  status: false,
  aiEnhanced: false
}
// Define a common device type
export type DeviceType = 'desktop' | 'mobile' | 'tablet'

export interface AnalyticsState {
  landingPages: {
    isFetching: boolean
    pages: (ExtendedLandingPage & { subAccountId: string })[]
    isFetched: boolean
    pageNumber: number
    totalLandings?: number
  }
  selectedLandingPageId?: string
  commonFilters: {
    selectedLandingPageId: string
    period: string
    funnel_id: funnelObj | null
    device_type: string;
  }
  analytics: {
    iframe_urls: {
      heat_map: string
      countries_regional_map: string
      [key: string]: string
    }
    speed_performance: {
      isFetching: boolean
      filters: SpeedPerformanceFilter
      cumulativeData: CumulativeData | null
      datewiseData: DatewiseData[]
      continents: ContinentsData[]
    }
    page_load_time: {
      isFetching: boolean
      filters: SpeedPerformanceFilter
      cumulativeData: CumulativeData | null
      datewiseData: DatewiseData[]
      continents: ContinentsData[]
    }
    server_response_time: {
      isFetching: boolean
      filters: SpeedPerformanceFilter
      cumulativeData: CumulativeData | null
      datewiseData: DatewiseData[]
      continents: ContinentsData[]
    }
    time_to_first_byte: {
      isFetching: boolean
      filters: SpeedPerformanceFilter
      cumulativeData: CumulativeData | null
      datewiseData: DatewiseData[]
      continents: ContinentsData[]
    }
    impact_on_user_engagement: {
      isFetching: boolean
      filters: SpeedPerformanceFilter
      data: Graph[]
      cumulativeData: ImpactOnUserEngagementCumulativeData | null
      continents: ContinentUserImpatchOnEngagementData[]
    }
    clickStream: {
      isFetching: boolean
      clickStramData: ClickStreamData[]
      clickStreamAnalyticData: ResponseClickStreams
      filters: ClickStreamFilter
    }
    userBehaviourAnalysis: {
      isFetching: boolean
      userBehaviourAnalysisData: ResponseUserBehaviourAnalysis[]
      filters: UserBehaviorAnalysisFilter
    }
    userBehaviourAnalysisTimeOnPage: {
      isFetching: boolean
      filters: UserBehaviorAnalysisTimeOnPageFilter
      userBehaviourTimeOnPageData: ResponseUserBehaviourTimeOnPage
    }
    userBehaviourAnalysisHoverEngagement: {
      isFetching: boolean
      filters: UserBehaviorAnalysisHoverEngagementFilter
      userBehaviourHoverEngagementData: ResponseUserBehaviourHoverEngagement
    }
    userBehaviourAnalysisHeatmap: {
      isFetching: boolean
      filters: UserBehaviorAnalysisHeatmapFilter
      userBehaviourHeatmapData: ResponseUserBehaviourHeatmap
    }
    userBehaviourAnalysisScrollDepth: {
      isFetching: boolean
      filters: UserBehaviorAnalysisScrollDepthFilter
      userBehaviourScrollDepthData: ResponseUserBehaviourScrollDepth
    }
    dropDownAnalysis: {
      isFetching: boolean
      dropDownAnalysisData: ResponseDropDownAnalysis[]
      filters: dropDownAnalysisFilter
    }

    predictive_modeling: PredictiveModelingState

    reginalMap: {
      isFetching: boolean
      RegionalMapData: ResponseRegionalMapData[]
      filters: RegionalMapFilter
      iframe_url?: string
    }
    top5ReginalMapCountryData: {
      data: Top5LocationData[]
      isFetching: boolean
      granularity: 'countries' | 'regions' | 'cities'
    }
    userEngagementImpact: {
      isFetching: boolean
      data: ResponseUserBehaviourAnalysis[]
    }
    dropOffByTrafficType: {
      isFetching: boolean
      data: ResponseDropOffByTrafficType | null
      filters: DropOffByTrafficTypeFilter
    }
    dropOffByVisitorType: {
      isFetching: boolean
      data: ResponseDropOffByVisitorType | null
      filters: DropOffByVisitorTypeFilter
    }
    dropOffOverTime: {
      isFetching: boolean
      data: DropOffAnalysisResponse | null
      filters: DropOffOverTimeFilter
    }
    dropOffPercentage: {
      isFetching: boolean
      data: DropOffPercentageStep[] | null
      filters: DropOffPercentageFilter
    }
    charts: {
      isFetching: boolean
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: any
    }
  }
}

export interface CumulativeData {
  min_server_time: number
  avg_server_time: number
  max_server_time: number
  good_server_time: string
  average_server_time: string
  bad_server_time: string
  min_page_load_time: number
  avg_page_load_time: number
  max_page_load_time: number
  good_page_load_time: string
  average_page_load_time: string
  bad_page_load_time: string
}

export interface filters{
    device_type:string,
    traffic_source:string,
    location_type:string,
    location:string,
    user_segment:string
}
export interface DatewiseData {
  label: string
  min_time_network: string
  max_time_network: string
  avg_time_network: number
  min_time_server: string
  max_time_server: string
  avg_time_server: number
  min_time_transfer: string
  max_time_transfer: string
  avg_time_transfer: number
  avg_time_on_load: number
  avg_page_load_time: number
  avg_time_to_first_byte: number
  url: string
  date: string
}

export interface ContinentEntry {
  [continent: string]: {
    'cumulative-data': CumulativeData[]
    'datewise-data': DatewiseData[]
  }
}

export interface ContinentUserImpatchOnEngagementEntry {
  [continent: string]: {
    graph: Graph[]
    cumulativeData: ImpactOnUserEngagementCumulativeData | null
  }
}

export interface ContinentUserImpatchOnEngagementData {
  continents: ContinentUserImpatchOnEngagementEntry[]
}

export interface ContinentsData {
  continents: ContinentEntry[]
}

// Add new interface for cumulative data for impact on user engagement
export interface ImpactOnUserEngagementCumulativeData {
  good_time_string: string
  average_time_string: string
  bad_time_string: string
  good_time: number
  bad_time: number
}

export interface Graph {
  bounce_rate: string
  avg_time_on_page: number
}

export interface ClickStreamDimension {
  clickX: number
  clickY: number
}

export interface ClickStreamData {
  label: string
  dimensions: ClickStreamDimension[]
  clickCount: number
}

export interface Prediction {
  date: string
  value: number
}

export interface RegionalPrediction {
  region: string
  predictions: Prediction[]
}

export enum device_type {
  desktop = 'desktop',
  tab = 'tablet',
  mobile = 'mobile',
}

export interface SpeedPerformanceFilter {
  device_type: device_type
  location_type: string
  locations: string[]
  period: string
  traffic_source: string
}

export interface ClickStreamFilter {
  date: string
  expanded: number
  filter_limit: number
  period: string
  type: string
  device_type?: device_type
}

export interface DropOffRateData {
  name: string
  data: {
    x: number // Converted avg_time_on_page in minutes
    originalSeconds: number // Original time in seconds
    y: number // Bounce rate percentage
  }[]
  goodTime: number // Threshold for good time
  badTime: number // Threshold for bad time
}

export interface dropDownAnalysisFilter {
  funnel_id: funnelObj
  period: string
  device_type: device_type
  traffic_source: string
  user_segment: string
  kpi: string
}

export interface RegionalMapFilter {
  period: string
  device_type: device_type
  user_segment: string
  location_type: string
  location: string
  traffic_source: string
}

export type UserBehaviorAnalysisFilter = SpeedPerformanceFilter & {
  funnel_id?: funnelObj
}
export type UserBehaviorAnalysisTimeOnPageFilter = UserBehaviorAnalysisFilter
export type UserBehaviorAnalysisHoverEngagementFilter =
  UserBehaviorAnalysisFilter
export type UserBehaviorAnalysisHeatmapFilter = UserBehaviorAnalysisFilter
export type UserBehaviorAnalysisScrollDepthFilter = UserBehaviorAnalysisFilter

export const dropDownAnalysisFilter: dropDownAnalysisFilter = {
  funnel_id: defaultFunnelObj,
  period: 'last 7 days',
  device_type: device_type.desktop,
  traffic_source: '',
  user_segment: '',
  kpi: ''
}

export interface HistoricalDataPoint {
  date: string
  value: number
}

export interface PredictedDataPoint {
  date: string
  value: number
}

export interface PredictiveModelingResponse {
  historical_data?: HistoricalDataPoint[]
  predicted_data?: PredictedDataPoint[]
}

export interface PredictiveModelingFilter {
  period: string
  kpi: string
  forecast_days: number
  date_range: string
  region: string
  device_type: device_type
  traffic_source: string
  location_type: string
  locations: string[]
  modeling_data_type: string
}

export interface PredictiveModelingState {
  data: PredictiveModelingResponse | null
  isFetching: boolean
  filters: PredictiveModelingFilter
}

export interface DropOffByTrafficTypeFilter {
  site_id?: number
  period?: string
  device_type?: device_type
}

export const dropOffByTrafficTypeInitialState: DropOffByTrafficTypeFilter = {
  site_id: 267,
  period: 'last 7 days',
  device_type: device_type.desktop
}

export interface DropOffByVisitorTypeFilter {
  site_id?: number
  period?: string
  device_type?: device_type
}

export const dropOffByVisitorTypeInitialState: DropOffByVisitorTypeFilter = {
  site_id: 267,
  period: 'last 7 days',
  device_type: device_type.desktop
}

// Payload interface
export interface DropOffAnalysisPayload {
  site_id?: number
  period?: string
  traffic_source?: string
  user_segment?: string
  kpi?: string
  device_type?: device_type
}

// Response interfaces
export interface DeviceData {
  device_type: 'desktop' | 'smartphone' | 'tablet' | 'mobile'
  exit_count: number
  exit_rate: string
}

export interface DatewiseEntry {
  date: string
  day: string
  devices: DeviceData[]
}

export interface DeviceDropOffData {
  exit_rate: string
  exit_count: number
}

export interface DropOffByDevice {
  device_type: 'desktop' | 'smartphone' | 'tablet' | 'mobile'
  data: DeviceDropOffData
}

export interface DropOffAnalysisResponse {
  datewise_data: DatewiseEntry[]
  drop_off_by_devices: DropOffByDevice[]
}

export interface DropOffOverTimeFilter {
  site_id: number
  period: string
  traffic_source: string
  user_segment: string
  kpi: string
  device_type?: device_type
}

export const dropOffOverTimeInitialState: DropOffOverTimeFilter = {
  site_id: 267,
  period: 'last 7 days',
  traffic_source: '',
  user_segment: '',
  kpi: '',
  device_type: device_type.desktop
}

export interface DropOffPercentageStep {
  label: string
  step_proceeded_rate: string
  step_exited_rate: string
}

export interface DropOffPercentageFilter {
  site_id: number
  funnel_id: funnelObj
  period: string
  device_type: string
  traffic_source: string
  user_segment: string
  kpi: string
}

export const dropOffPercentageInitialState: DropOffPercentageFilter = {
  site_id: 267,
  funnel_id: defaultFunnelObj,
  period: 'last 7 days',
  device_type: 'desktop',
  traffic_source: '',
  user_segment: '',
  kpi: ''
}

// export const predictiveModelingInitialState: PredictiveModelingFilter = {
//   device_type: device_type.desktop,
//   traffic_source: '',
//   location_type: '',
//   locations: [],
//   kpi: 'nb_visits',
//   forecast_days: 30,
//   date_range: 30,
//   region: '',
//   period: 'last month'
// data: [forecast_days: number, date_range: number]
export const predictiveModelingInitialState: PredictiveModelingState = {
  data: null,
  isFetching: false,
  filters: {
    period: 'last month',
    kpi: 'nb_visits',
    forecast_days: 30,
    date_range: 'last30',
    region: '',
    device_type: device_type.desktop,
    traffic_source: 'all',
    location_type: '',
    locations: [],
    modeling_data_type: 'predictive, historical'
  }
}

export const speedPerformanceInitialState: SpeedPerformanceFilter = {
  device_type: device_type.desktop,
  location_type: '',
  locations: [],
  period: 'last month',
  traffic_source: ''
}

export const clickStreamFilter: ClickStreamFilter = {
  period: 'last 7 days',
  date: 'today',
  filter_limit: -1,
  expanded: 1,
  type: 'page',
  device_type: device_type.desktop
}

export const userBehaviorFilterInitialState = {
  ...speedPerformanceInitialState,
  funnel_id: defaultFunnelObj
}

export const analyticsPeriodFilterStates = ['last 7 days', 'last month']

export const regionalMapFilter: RegionalMapFilter = {
  device_type: device_type.desktop,
  location: '',
  location_type: '',
  period: analyticsPeriodFilterStates[1],
  user_segment: '',
  traffic_source: ''
}
export const defaultLandingSubPage: LandingSubPageData = {
  pageId: '',
  pageName: null,
  pageDescription: null,
  status: false,
  contentJson: null,
  createdAt: new Date(),
  updatedAt: new Date(),
  landingPageId: '',
  siteHsrId: 0,
  funnelId: 0
}

export interface CommonFilters {
  selectedLandingPageId?: string
  funnel_id?: funnelObj
  period?: string
  device_type?: string
  [key: string]: unknown
}

export const initialAnalyticsState: AnalyticsState = {
  landingPages: {
    isFetching: true,
    pages: [],
    isFetched: false,
    pageNumber: 1,
    totalLandings: 0
  },
  // selectedLandingPageId: '',
  commonFilters: {
    selectedLandingPageId: '',
    period: 'last month',
    funnel_id: null,
    device_type: 'desktop'
  },
  analytics: {
    iframe_urls: {
      heat_map: '',
      countries_regional_map: ''
    },
    speed_performance: {
      isFetching: true,
      filters: speedPerformanceInitialState,
      cumulativeData: null,
      datewiseData: [],
      continents: []
    },
    page_load_time: {
      isFetching: true,
      filters: speedPerformanceInitialState,
      cumulativeData: null,
      datewiseData: [],
      continents: []
    },
    server_response_time: {
      isFetching: true,
      filters: speedPerformanceInitialState,
      cumulativeData: null,
      datewiseData: [],
      continents: []
    },
    time_to_first_byte: {
      isFetching: true,
      filters: speedPerformanceInitialState,
      cumulativeData: null,
      datewiseData: [],
      continents: []
    },
    impact_on_user_engagement: {
      isFetching: false,
      filters: speedPerformanceInitialState,
      cumulativeData: null,
      continents: [],
      data: []
    },
    clickStream: {
      isFetching: true,
      clickStramData: [],
      clickStreamAnalyticData: {
        'click-stream': [
          {
            label: '',
            'click-dimension': [],
            'click-count': 0
          }
        ],
        'top-click-areas': [
          {
            label: '',
            type: '',
            total_clicks: 0
          }
        ]
      },
      filters: clickStreamFilter
    },
    userBehaviourAnalysis: {
      isFetching: true,
      userBehaviourAnalysisData: [],
      filters: userBehaviorFilterInitialState
    },
    userBehaviourAnalysisHeatmap: {
      isFetching: true,
      filters: userBehaviorFilterInitialState,
      userBehaviourHeatmapData: {
        elements: []
      }
    },
    userBehaviourAnalysisHoverEngagement: {
      isFetching: true,
      filters: userBehaviorFilterInitialState,
      userBehaviourHoverEngagementData: {
        nb_of_users: 0,
        avg_duration_on_page: 0,
        nb_of_actions: 0
      }
    },
    userBehaviourAnalysisTimeOnPage: {
      isFetching: true,
      filters: userBehaviorFilterInitialState,
      userBehaviourTimeOnPageData: [] as ResponseUserBehaviourTimeOnPage
    },
    userBehaviourAnalysisScrollDepth: {
      isFetching: true,
      filters: userBehaviorFilterInitialState,
      userBehaviourScrollDepthData: {
        cumulative_data: {},
        daily_data: {}
      }
    },
    dropDownAnalysis: {
      isFetching: true,
      dropDownAnalysisData: [],
      filters: dropDownAnalysisFilter
    },
    predictive_modeling: {
      ...predictiveModelingInitialState,
      isFetching: true
    },
    reginalMap: {
      isFetching: true,
      RegionalMapData: [],
      filters: regionalMapFilter
    },
    top5ReginalMapCountryData: {
      data: [],
      isFetching: true,
      granularity: 'countries'
    },
    userEngagementImpact: {
      isFetching: true,
      data: []
    },
    dropOffByTrafficType: {
      isFetching: true,
      data: null,
      filters: dropOffByTrafficTypeInitialState
    },
    dropOffByVisitorType: {
      isFetching: true,
      data: null,
      filters: dropOffByVisitorTypeInitialState
    },
    dropOffOverTime: {
      isFetching: true,
      data: null,
      filters: dropOffOverTimeInitialState
    },
    dropOffPercentage: {
      isFetching: true,
      data: null,
      filters: dropOffPercentageInitialState
    },
    charts: {
      isFetching: true,
      data: []
    }
  }
}

export type AnalyticsAction =
  | {
      type: 'SAVE_LANDINGS'
      payload: {
        pages: (LandingPage & { subAccountId: string })[]
        pageNumber: number
        totalLandings?: number
      }
    }
  | { type: 'RESET_ALL_FETCHING_STATES' }
  | {
      type: 'APPEND_LANDINGS'
      payload: {
        pages: (LandingPage & { subAccountId: string })[]
        pageNumber: number
        totalLandings?: number
      }
    }
  |
  {
    type: 'SELECT_LANDING_PAGE'
    payload: CommonFilters
  }
  
  
  | {
      type: 'SET_IFRAMES'
      payload: {
        heat_map: string
        countries_regional_map: string
        [key: string]: string
      }
    }
  | {
      type: 'RESET_IFRAMES_HEATMAP'
      payload: {
        heat_map: string
      }
    }
  | {
      type: 'SET_REGIONAL_MAP_IFRAMES'
      payload: {
        iframe_url: string
      }
    }
  | {
      type: 'SET_REGIONAL_MAP_LOADING'
    }
  | {
      type: 'UPDATE_FUNNEL_ID_FILTER'
      payload: {
        funnel_id: funnelObj
      }
    }
  | {
      type: 'UPDATE_FILTERS'
      payload:
        | {
            graphType: 'speed_performance'
            filters: Partial<SpeedPerformanceFilter>
          }
        | {
            graphType: 'impact_on_user_engagement'
            filters: Partial<SpeedPerformanceFilter>
          }
        | { graphType: 'clickStream'; filters: Partial<ClickStreamFilter> }
        | {
            graphType: 'userBehaviourAnalysis'
            filters: Partial<UserBehaviorAnalysisFilter>
          }
        | {
            graphType: 'dropDownAnalysis'
            filters: Partial<dropDownAnalysisFilter>
          }
        | {
            graphType: 'predictive_modeling'
            filters: Partial<PredictiveModelingFilter>
          }
        | {
            graphType: 'userBehaviourAnalysisTimeOnPage'
            filters: Partial<UserBehaviorAnalysisTimeOnPageFilter>
          }
        | {
            graphType: 'userBehaviourAnalysisHoverEngagement'
            filters: Partial<UserBehaviorAnalysisHoverEngagementFilter>
          }
        | {
            graphType: 'userBehaviourAnalysisHeatmap'
            filters: Partial<UserBehaviorAnalysisHeatmapFilter>
          }
        | {
            graphType: 'userBehaviourAnalysisScrollDepth'
            filters: Partial<UserBehaviorAnalysisScrollDepthFilter>
          }
        | {
            graphType: 'dropOffOverTime'
            filters: Partial<DropOffOverTimeFilter>
          }
        | {
            graphType: 'dropOffByTrafficType'
            filters: Partial<DropOffByTrafficTypeFilter>
          }
        | {
            graphType: 'dropOffByVisitorType'
            filters: Partial<DropOffByVisitorTypeFilter>
          }
        | {
            graphType: 'dropOffPercentage'
            filters: Partial<DropOffPercentageFilter>
          }
        | {
            graphType: 'reginalMap'
            filters: Partial<RegionalMapFilter>
          }
        | {
            graphType: 'page_load_time'
            filters: Partial<SpeedPerformanceFilter>
          }
        | {
            graphType: 'server_response_time'
            filters: Partial<SpeedPerformanceFilter>
          }
        | {
            graphType: 'time_to_first_byte'
            filters: Partial<SpeedPerformanceFilter>
          }
    }
  | {
      type: 'SET_SPEED_PERFORMACE_DATA'
      payload: {
        cumulativeData?: CumulativeData | null
        datewiseData?: DatewiseData[]
        continents?: ContinentsData[]
      }
    }
  | {
      type: 'SET_PAGE_LOAD_TIME_DATA'
      payload: {
        cumulativeData?: CumulativeData | null
        datewiseData?: DatewiseData[]
        continents?: ContinentsData[]
      }
    }
  | {
      type: 'SET_SERVER_RESPONSE_TIME_DATA'
      payload: {
        cumulativeData?: CumulativeData | null
        datewiseData?: DatewiseData[]
        continents?: ContinentsData[]
      }
    }
  | {
      type: 'SET_TIME_TO_FIRST_BYTE_DATA'
      payload: {
        cumulativeData?: CumulativeData | null
        datewiseData?: DatewiseData[]
        continents?: ContinentsData[]
      }
    }
  | {
      type: 'SET_IMPACT_ON_USER_ENGAGEMENT_DATA'
      payload: {
        graph: Graph[]
        cumulativeData: ImpactOnUserEngagementCumulativeData | null
        continents?: ContinentUserImpatchOnEngagementData[]
      }
    }
  | {
      type: 'SET_CLICK_STREAM_DATA'
      payload: { clickStreamData: ClickStreamData[] }
    }
  | {
      type: 'SET_CLICK_STREAM_ANALYTICS_DATA'
      payload: {
        clickStreamAnalyticData: ResponseClickStreams
      }
    }
  | {
      type: 'SET_USER_BEHAVIOUR_ANALYSIS_DATA'
      payload: ResponseUserBehaviourAnalysis[]
    }
  | {
      type: 'SET_USER_BEHAVIOUR_HOVER_ENGAGEMENT_DATA'
      payload: ResponseUserBehaviourHoverEngagement
    }
  | {
      type: 'SET_USER_BEHAVIOUR_HEATMAP_DATA'
      payload: ResponseUserBehaviourHeatmap
    }
  | {
      type: 'SET_USER_BEHAVIOUR_TIME_ON_PAGE_DATA'
      payload: ResponseUserBehaviourTimeOnPage
    }
  | {
      type: 'SET_USER_BEHAVIOUR_SCROLL_DEPTH_DATA'
      payload: ResponseUserBehaviourScrollDepth
    }
  | {
      type: 'SET_TOP_5_COUNTRIES_REGIONAL_MAPS_DATA'
      payload: Top5LocationData[]
    }
  | {
      type: 'UPDATE_TOP_5_GRANULARITY'
      payload: 'countries' | 'regions' | 'cities'
    }
  | {
      type: 'SET_USER_ENGAGEMENT_IMPACT'
      payload: ResponseUserBehaviourAnalysis[]
    }
  | {
      type: 'SET_DROP_OFF_ANALYSIS_DATA'
      payload: ResponseDropDownAnalysis[]
    }
  | {
      type: 'SET_PREDICTIVE_MODELING_DATA'
      payload: PredictiveModelingResponse | null
    }
  | {
      type: 'UPDATE_FILTERS_FOR_ALL'
      payload:
        | Partial<SpeedPerformanceFilter>
        | Partial<UserBehaviorAnalysisFilter>
        | Partial<ClickStreamFilter>
        | Partial<dropDownAnalysisFilter>
        | Partial<PredictiveModelingFilter>
        | Partial<UserBehaviorAnalysisTimeOnPageFilter>
        | Partial<UserBehaviorAnalysisHoverEngagementFilter>
        | Partial<UserBehaviorAnalysisHeatmapFilter>
        | Partial<UserBehaviorAnalysisScrollDepthFilter>
        | Partial<RegionalMapFilter>
        | Partial<UserBehaviorAnalysisHeatmapFilter>
    }
  | {
      type: 'CLEAR_FILTERS_FOR_ALL'
    }
  | {
      type: 'SET_REGIONAL_MAP_DATA'
      payload: ResponseRegionalMapData[]
    }
  | {
      type: 'SET_DROP_OFF_ANALYSIS_BY_TRAFFIC_TYPE_DATA'
      payload: ResponseDropOffByTrafficType | null
    }
  | {
      type: 'SET_DROP_OFF_ANALYSIS_BY_VISITOR_TYPE_DATA'
      payload: ResponseDropOffByVisitorType | null
    }
  | {
      type: 'SET_DROP_OFF_OVER_TIME_DATA'
      payload: DropOffAnalysisResponse | null
    }
  | {
      type: 'SET_DROP_OFF_PERCENTAGE_DATA'
      payload: DropOffPercentageStep[] | null
    }
  | {
      type: 'SET_EXPORTABLE_CHARTS_DATA'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      payload: any | []
    }
  |  
  { type: 'UPDATE_COMMON_FILTERS'; payload: Partial<AnalyticsState['commonFilters']> }

export type analyticsType =
  | 'speed_performance'
  | 'page_load_time'
  | 'server_response_time'
  | 'time_to_first_byte'
  | 'clickStream'
  | 'userBehaviourAnalysis'
  | 'dropDownAnalysis'
  | 'predictive_modeling'
  | 'userBehaviourAnalysisTimeOnPage'
  | 'userBehaviourAnalysisHoverEngagement'
  | 'userBehaviourAnalysisHeatmap'
  | 'userBehaviourAnalysisScrollDepth'
  | 'dropOffByTrafficType'
  | 'dropOffByVisitorType'
  | 'dropOffOverTime'
  | 'dropOffPercentage'
  | 'reginalMap'

export const formatSpeedperformanceData = (
  speed_performance: {
    filters: SpeedPerformanceFilter
    cumulativeData: CumulativeData | null
    datewiseData: DatewiseData[]
    continents: ContinentsData[]
  },
  avg_page_load: boolean = true,
  avg_server_respnse: boolean = true
) => {
  const { datewiseData, cumulativeData, filters, continents } =
    speed_performance
  // console.log('datewiseData', datewiseData)
  // console.log('cumulativeData', cumulativeData)
  // Format categories based on period
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let categories = [] as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const series = [] as any
  let maxValue

  const avgPageLoadTime = cumulativeData?.average_page_load_time
    ? parseFloat(cumulativeData?.average_page_load_time?.split(' ')[0])
    : cumulativeData?.avg_page_load_time || 0

  // Predefined color map for continents
  if (continents?.length > 0) {
    const uniqueDates = new Set<string>() // Store unique dates

    const formatDate = (dateString: string) => {
      if (filters.period === 'last 7 days') {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', { weekday: 'short' }) // Example: "Mon"
      }
      const date = new Date(dateString)
      const day = date.getDate()
      const month = date.toLocaleDateString('en-US', { month: 'short' })
      const year = date.getFullYear()
      const formattedDate = `${day} ${month}, ${year}`
      return formattedDate
    }

    continents?.forEach((continentObj: ContinentsData) => {
      Object.values(continentObj).forEach(({ 'datewise-data': data }) => {
        data.forEach((entry: DatewiseData) =>
          uniqueDates.add(formatDate(entry.date))
        ) // Add formatted date
      })
    })

    categories = Array.from(uniqueDates) // Sorted unique dates for x-axis
    let counter = 0
    continents?.forEach((continentObj: ContinentsData) => {
      Object.entries(continentObj).forEach(([continentName, data]) => {
        counter++
        const datewiseData = data['datewise-data']
        const regionStyle = REGION_STYLES[
          continentName as keyof typeof REGION_STYLES
        ] ||
          RANDOM_COLOR_PALETTE[counter] || {
            lineColor: '#000000',
            gradientFrom: 'rgba(0,0,0,0.45)',
            gradientTo: 'rgba(0,0,0,0.05)',
            dashArray: 0
          }

        if (avg_page_load) {
          series.push({
            name: `Avg Page Load - ${continentName}`,
            data: categories.map((date: string) => {
              const match = datewiseData.find(
                (entry: DatewiseData) => formatDate(entry.date) === date
              )
              return match ? match.avg_page_load_time : 0 // Ensure all dates have values
            }),
            color: regionStyle.lineColor
          })
        }

        if (avg_server_respnse) {
          series.push({
            name: `Avg Server Response - ${continentName}`,
            data: categories.map((date: string) => {
              const match = datewiseData.find(
                (entry: DatewiseData) => formatDate(entry.date) === date
              )

              return match ? match.avg_time_server : 0
            }),
            color: regionStyle.lineColor
          })
        }
      })
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    maxValue = Math.max(
      ...(series?.flatMap((s: { data: number[] }) => s?.data ?? []) ?? []),
      avgPageLoadTime ?? 0
    )
  } else {
    categories = datewiseData?.map(data => {
      if (filters.period === 'last 7 days') {
        // Convert date string to day name
        const date = new Date(data.date)
        return date.toLocaleDateString('en-US', { weekday: 'short' }) // Returns: Mon, Tue, etc.
      }
      // return data.date // Keep original date format for other periods
      const date = new Date(data.date)
      const day = date.getDate()
      const month = date.toLocaleDateString('en-US', { month: 'short' })
      const year = date.getFullYear()
      const formattedDate = `${day} ${month}, ${year}`
      return formattedDate
    })
    if (avg_page_load) {
      series.push({
        name: 'Avg Page Load Time',
        data: datewiseData?.map(data => data.avg_page_load_time),
        color: '#2E6CFF' // Blue color for page load time
      })
    }

    if (avg_server_respnse) {
      series.push({
        name: 'Avg Server Response Time',
        data: datewiseData?.map(data => data.avg_time_server),
        color: '#A4DE1B' // Green color for server response time
      })
    }

    maxValue = Math.max(
      ...(avg_page_load ? [cumulativeData?.max_page_load_time || 0] : []),
      ...(avg_server_respnse ? [cumulativeData?.max_server_time || 0] : [])
    )
  }

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: false
      },
      background: '#FFFFFF',
      fontFamily: 'Inter, sans-serif'
    },
    plotOptions: {
      bar: {
        // borderRadius: 4,
        columnWidth: '40%',
        dataLabels: {
          position: 'top'
        },
        distributed: false
      }
    },
    dataLabels: {
      enabled: false
    },
    grid: {
      borderColor: '#F1F4F9', // Main grid color
      strokeDashArray: 0,
      position: 'back',
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      }
    },
    annotations: {
      yaxis: [
        {
          y: avgPageLoadTime,
          borderColor: '#54AA19',
          label: {
            text: 'Good',
            borderColor: 'none',
            position: 'right',
            offsetY: 0,
            style: {
              // color: '#54AA19',
              background: 'transparent'
            }
          },
          strokeDashArray: 5
        },
        {
          y: 3.5,
          borderColor: '#FAD801',
          label: {
            text: 'Average',
            borderColor: 'none',
            position: 'right',
            offsetX: -50,
            style: {
              background: 'transparent'
            }
          },
          strokeDashArray: 5
        },
        {
          y: 4.0,
          borderColor: '#FF5C39',
          label: {
            text: 'Bad',
            borderColor: 'none',
            position: 'right',
            offsetX: -120,
            style: {
              // color: '#FF5C39',
              background: 'transparent'
            }
          },
          strokeDashArray: 5
        }
      ]
    },
    xaxis: {
      categories,
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      }
    },
    yaxis: {
      min: 0,
      max: maxValue + 1,
      tickAmount: 4,
      title: {
        text: 'Average Load Time',
        style: {
          fontSize: '14px',
          fontWeight: 400
        }
      },
      labels: {
        formatter: (value: number) => `${value?.toFixed(3)}s`
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value: number) => `${value?.toFixed(3)}s`
      }
    },
    legend: {
      show: continents?.length > 0 ? true : false,
      position: 'bottom',
      horizontalAlign: 'left',
      formatter: seriesName =>
        seriesName.replace(/(Avg Page Load - |Avg Server Response - )/g, ''),
      onItemClick: {
        toggleDataSeries: false
      }
    },
    theme: {
      mode: 'light' as const
    }
  }

  return { series, options }
}

export function getRandomLightColorWithOpacity(): string {
  // Generate random values for red, green, and blue, ensuring they are in the lighter range (128–255)
  const r = Math.floor(178 + Math.random() * 78) // 128–255
  const g = Math.floor(178 + Math.random() * 78) // 128–255
  const b = Math.floor(178 + Math.random() * 78) // 128–255

  // Convert to rgba format with opacity 0.3
  return `rgba(${r}, ${g}, ${b}, 0.3)`
}

export const formatClickStreamData = (clickStream: ClickStreamData[]) => {
  const labels = clickStream.map(item => item.label)

  const chartData = {
    datasets: clickStream.map((clickStreamData, index) => ({
      label: clickStreamData.label,
      data: clickStreamData.dimensions.map(dimension => ({
        x: dimension.clickX + index * 100,
        y: dimension.clickY,
        clickCount: clickStreamData?.clickCount,
        label: clickStreamData?.label
      })),
      backgroundColor: 'rgba(138, 43, 226, 0.6)'
    }))
    // datasets: [
    //   {
    //     label: 'Click Stream Data',
    //     data: scatterData,
    //     backgroundColor: 'rgba(138, 43, 226, 0.6)' // Purple color for dots
    //   }
    // ]
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'white',
        titleColor: 'black',
        bodyColor: 'black',
        usePointStyle: true,
        borderWidth: 1,
        borderRadius: 4,
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: (context: any) => {
            const label = context?.raw?.label
            const clickCount = context?.raw?.clickCount
            return [`${label}`, `${clickCount} Clicks`]
          }
        }
      },
      usePointStyle: true,
      pointStyle: 'circle'
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          drawTicks: false,
          drawOnChartArea: false
        },
        title: {
          display: false
        },
        ticks: {
          stepSize: 100,
          align: 'center',
          callback: (value: number) => {
            return labels[Math.floor(value / 100)]
          },
          padding: 10
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          drawTicks: false,
          drawOnChartArea: false
        },
        ticks: {
          display: false // Hides Y-axis numbers
        },
        title: {
          display: false,
          text: 'Click Intensity'
        }
      }
    }
  }

  const backgroundPlugin = {
    id: 'scattered-chart',
    beforeDraw: (chart: Chart) => {
      const canvas = chart.canvas
      const ctx = canvas.getContext('2d')
      const chartArea = chart.chartArea

      if (!ctx) {
        console.error('Canvas context is null')
        return
      }

      const colors = [
        'rgba(255, 99, 132, 0.3)',
        'rgba(54, 162, 235, 0.3)',
        'rgba(255, 206, 86, 0.3)',
        'rgba(75, 192, 192, 0.3)',
        'rgba(153, 102, 255, 0.3)',
        'rgba(255, 159, 64, 0.3)',
        'rgba(100, 100, 100, 0.3)',
        'rgba(255, 165, 0, 0.3)',
        'rgba(50, 205, 50, 0.3)',
        'rgba(255, 20, 147, 0.3)',
        'rgba(255, 99, 71, 0.3)',
        'rgba(32, 178, 170, 0.3)',
        'rgba(186, 85, 211, 0.3)',
        'rgba(139, 0, 255, 0.3)',
        'rgba(128, 0, 128, 0.3)',
        'rgba(0, 128, 128, 0.3)',
        'rgba(0, 255, 255, 0.3)',
        'rgba(240, 230, 140, 0.3)',
        'rgba(128, 128, 0, 0.3)',
        'rgba(255, 215, 0, 0.3)',
        'rgba(255, 69, 0, 0.3)',
        'rgba(70, 130, 180, 0.3)',
        'rgba(255, 105, 180, 0.3)',
        'rgba(0, 255, 127, 0.3)',
        'rgba(255, 140, 0, 0.3)',
        'rgba(255, 182, 193, 0.3)',
        'rgba(135, 206, 250, 0.3)',
        'rgba(192, 192, 192, 0.3)'
      ]
      const width = chartArea.right - chartArea.left
      const sectionWidth = width / labels.length
      labels.forEach((label, index) => {
        ctx.save()
        const color = colors[index % colors.length]
        ctx.fillStyle = color
        ctx.fillRect(
          chartArea.left + index * sectionWidth,
          chartArea.top,
          sectionWidth,
          chartArea.bottom - chartArea.top
        )
        ctx.restore()
      })
    }
  }

  return { chartData, options, backgroundPlugin }
}

export const formatUserBehaviorAnalysis = (
  data: ResponseUserBehaviourAnalysis[]
) => {
  const labels = data.map(
    (item, index) =>
      `${Object.keys(item)[0]} : ${Object.values(data[index])[0]}`
  )
  const percentages = data.map(item =>
    parseFloat(Object.values(item)[0].replace('%', ''))
  )

  const isEmptyData = percentages.every(item => item === 0)

  const backgroundColors = [
    '#4285F4', // Blue
    '#34A853', // Green
    '#FBBC04', // Yellow
    '#EA4335', // Red
    '#AB47BC' // Purple
  ]

  const backgroundHoverColors = [
    '#357AE8', // Darker Blue
    '#2C9E49', // Darker Green
    '#F9A825', // Darker Yellow
    '#D93025', // Darker Red
    '#9C27B0' // Darker Purple
  ]

  // Chart data configuration
  const chartData = {
    labels: isEmptyData ? ['', ...labels] : labels, // Only show meaningful labels
    datasets: [
      {
        data: isEmptyData ? [1, ...percentages] : percentages, // Filter out 0% values
        backgroundColor: isEmptyData
          ? ['#E0E0E0', ...backgroundColors]
          : backgroundColors,
        hoverBackgroundColor: isEmptyData
          ? ['#E0E0E0', ...backgroundHoverColors]
          : backgroundHoverColors,
        borderWidth: 2
      }
    ]
  }

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const
      },
      tooltip: {
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: (tooltipItem: any) => {
            const label = chartData.labels[tooltipItem.dataIndex]
            return `${label}`
          }
        }
      }
    }
  }
  return { chartData, options }
}

export const formatImpactOnEngagementData = (impact_on_user_engagement: {
  data: Graph[]
  filters: SpeedPerformanceFilter
  cumulativeData: ImpactOnUserEngagementCumulativeData | null
  continents: ContinentUserImpatchOnEngagementData[]
}) => {
  const {
    data = [],
    filters,
    cumulativeData,
    continents
  } = impact_on_user_engagement
  const safeData = Array.isArray(data) ? data : []

  // General good and bad times
  const generalGoodTime = cumulativeData?.good_time || 2
  const generalBadTime = cumulativeData?.bad_time || 4
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const series = [] as any

  if (continents.length > 0) {
    continents.forEach((continent: ContinentUserImpatchOnEngagementData) => {
      Object.entries(continent).forEach(([continentName, data]) => {
        const pointData = data?.graph?.map((item: Graph) => ({
          x: item?.avg_time_on_page,
          originalSeconds: item?.avg_time_on_page,
          y: item?.bounce_rate
            ? parseFloat(item?.bounce_rate?.replace('%', ''))
            : 0
        }))

        series.push({
          name: `${continentName} Drop off rate`,
          type: 'scatter',
          data: pointData
        })

        series.push({
          name: `${continentName} Trend Line`,
          type: 'line',
          data: pointData,
          color: '#9B56FE',
          dashArray: 5
        })
      })
    })
  } else {
    const pointData = safeData.map(item => ({
      x: item?.avg_time_on_page,
      originalSeconds: item?.avg_time_on_page,
      y: item?.bounce_rate ? parseFloat(item?.bounce_rate?.replace('%', '')) : 0
    }))

    series.push({
      name: 'Drop off rate',
      type: 'scatter',
      data: pointData
    })
    series.push({
      name: 'Trend Line',
      type: 'line',
      data: pointData,
      color: '#9B56FE',
      dashArray: 5
    })
  }

  const maxTime = Math.max(...safeData.map(item => item?.avg_time_on_page))

  const options: ApexOptions = {
    chart: {
      type: 'scatter',
      zoom: { enabled: false },
      toolbar: { show: false },
      background: '#FFFFFF',
      fontFamily: 'Inter, sans-serif'
    },
    legend: {
      show: false
    },
    stroke: {
      curve: 'smooth',
      width: 2,
      colors: ['transparent', '#9B56FE'],
      dashArray: [0, 5]
    },
    markers: {
      size: 6,
      colors: ['#9B56FE'],
      strokeWidth: 0
    },
    grid: {
      show: true,
      borderColor: '#E5E7EB',
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    },
    xaxis: {
      min: 0,
      max: maxTime + 0.5,
      tickAmount: 11,
      title: {
        text: 'Average Load Time',
        style: { fontSize: '14px', fontWeight: 400 }
      },
      labels: {
        formatter: (val: string) => `${parseFloat(val).toFixed(1)}s`
      }
    },
    yaxis: {
      min: 0,
      max: 100,
      title: {
        text: 'Drop off rate',
        style: { fontSize: '14px', fontWeight: 400 }
      },
      labels: {
        formatter: (val: number) => `${val.toFixed(0)}%`
      }
    },
    tooltip: {
      shared: false,
      intersect: true,
      x: {
        show: true,
        formatter: () =>
          filters.locations?.length
            ? filters.locations.join(', ')
            : 'World Wide'
      },
      y: {
        formatter: value => `${value}% at ${value}s`
      }
    },
    annotations: {
      xaxis: [
        {
          x: 0,
          x2: generalGoodTime,
          fillColor: 'rgba(29, 177, 24, 0.06)',
          opacity: 1,
          borderColor: '#1DB118'
        },
        {
          x: generalGoodTime,
          x2: generalBadTime,
          fillColor: 'rgba(255, 222, 46, 0.06)',
          opacity: 1,
          borderColor: '#FFDE2E'
        },
        {
          x: generalBadTime,
          x2: maxTime + 0.5,
          fillColor: 'rgba(255, 84, 46, 0.06)',
          opacity: 1,
          borderColor: '#FF542E'
        },
        // Vertical dotted lines
        {
          x: generalGoodTime,
          strokeDashArray: 3,

          borderColor: '#1DB118',
          label: {
            // text: 'Good',
            orientation: 'vertical',
            borderColor: 'none',
            style: {
              background: 'transparent'
            }
          }
        },
        {
          x: generalBadTime,
          strokeDashArray: 3,
          label: {
            // text: 'Average',
            orientation: 'vertical',
            borderColor: 'none',
            style: {
              background: 'transparent'
            }
          }
        }
      ]
    }
  }

  return { series, options }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const REGION_STYLES = {
  Europe: {
    lineColor: '#2E6CFF',
    gradientFrom: 'rgba(46, 108, 255, 0.45)',
    gradientTo: 'rgba(46, 108, 255, 0.05)',
    dashArray: 0
  },
  Asia: {
    lineColor: '#EB56FE',
    gradientFrom: 'rgba(235, 86, 254, 0.45)',
    gradientTo: 'rgba(235, 86, 254, 0.05)',
    dashArray: 5
  },
  'North America': {
    lineColor: '#FF6B6B',
    gradientFrom: 'rgba(255, 107, 107, 0.45)',
    gradientTo: 'rgba(255, 107, 107, 0.05)',
    dashArray: 0
  },
  'South America': {
    lineColor: '#4ECDC4',
    gradientFrom: 'rgba(78, 205, 196, 0.45)',
    gradientTo: 'rgba(78, 205, 196, 0.05)',
    dashArray: 5
  },
  Africa: {
    lineColor: '#FFD93D',
    gradientFrom: 'rgba(255, 217, 61, 0.45)',
    gradientTo: 'rgba(255, 217, 61, 0.05)',
    dashArray: 0
  },
  Oceania: {
    lineColor: '#95A5A6',
    gradientFrom: 'rgba(149, 165, 166, 0.45)',
    gradientTo: 'rgba(149, 165, 166, 0.05)',
    dashArray: 5
  },
  'Middle East': {
    lineColor: '#6C5CE7',
    gradientFrom: 'rgba(108, 92, 231, 0.45)',
    gradientTo: 'rgba(108, 92, 231, 0.05)',
    dashArray: 0
  },
  Caribbean: {
    lineColor: '#00B894',
    gradientFrom: 'rgba(0, 184, 148, 0.45)',
    gradientTo: 'rgba(0, 184, 148, 0.05)',
    dashArray: 5
  },
  'Central America': {
    lineColor: '#FD79A8',
    gradientFrom: 'rgba(253, 121, 168, 0.45)',
    gradientTo: 'rgba(253, 121, 168, 0.05)',
    dashArray: 0
  },
  Antarctica: {
    lineColor: '#A8E6CF',
    gradientFrom: 'rgba(168, 230, 207, 0.45)',
    gradientTo: 'rgba(168, 230, 207, 0.05)',
    dashArray: 5
  }
} as const

const RANDOM_COLOR_PALETTE = [
  {
    lineColor: '#FF5733',
    gradientFrom: 'rgba(255, 87, 51, 0.45)',
    gradientTo: 'rgba(255, 87, 51, 0.05)',
    dashArray: 0
  },
  {
    lineColor: '#33FF57',
    gradientFrom: 'rgba(51, 255, 87, 0.45)',
    gradientTo: 'rgba(51, 255, 87, 0.05)',
    dashArray: 5
  },
  {
    lineColor: '#5733FF',
    gradientFrom: 'rgba(87, 51, 255, 0.45)',
    gradientTo: 'rgba(87, 51, 255, 0.05)',
    dashArray: 0
  },
  {
    lineColor: '#FFD700',
    gradientFrom: 'rgba(255, 215, 0, 0.45)',
    gradientTo: 'rgba(255, 215, 0, 0.05)',
    dashArray: 5
  },
  {
    lineColor: '#FF69B4',
    gradientFrom: 'rgba(255, 105, 180, 0.45)',
    gradientTo: 'rgba(255, 105, 180, 0.05)',
    dashArray: 0
  }
]

export const formatPredictiveModelingData = (
  data: PredictiveModelingResponse | null,
  period: string,
  modelingType: string
) => {
  if (
    !data ||
    (!data.historical_data?.length && !data.predicted_data?.length)
  ) {
    return { series: [], options: {} }
  }

  const series = []
  const commonColor = '#7161D2'

  // Add historical data series if available and requested
  if (modelingType.includes('historical') && data.historical_data?.length) {
    series.push({
      name: 'Historical Visits',
      type: 'area',
      data: data.historical_data.map(point => ({
        x: new Date(point.date).getTime(),
        y: point.value,
        isHistorical: true
      }))
    })
  }

  // Add predicted data series if available and requested
  if (modelingType.includes('predictive') && data.predicted_data?.length) {
    series.push({
      name: 'Predicted Visits',
      type: 'area',
      data: data.predicted_data.map(point => ({
        x: new Date(point.date).getTime(),
        y: point.value,
        isHistorical: false
      }))
    })
  }

  const options: ApexOptions = {
    chart: {
      type: 'area',
      height: 350,
      toolbar: {
        show: false
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'straight',
      width: 2,
      colors: [commonColor, commonColor],
      dashArray: [0, 5]
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'vertical',
        shadeIntensity: 0.6,
        opacityFrom: 0.6,
        opacityTo: 0.0,
        stops: [-78.77, 95.98],
        colorStops: [
          [
            {
              offset: 0,
              color: 'rgba(113, 97, 210, 0.60)',
              opacity: 0.6
            },
            {
              offset: 100,
              color: 'rgba(113, 97, 210, 0.00)',
              opacity: 0
            }
          ],
          [
            {
              offset: 0,
              color: 'rgba(113, 97, 210, 0.60)',
              opacity: 0.6
            },
            {
              offset: 100,
              color: 'rgba(113, 97, 210, 0.00)',
              opacity: 0
            }
          ]
        ]
      }
    },
    colors: [commonColor, commonColor],
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeFormatter: {
          year: 'yyyy',
          month: "MMM 'yy",
          day: 'dd MMM'
        },
        style: {
          fontSize: '14px',
          fontWeight: 400
        }
      }
    },
    yaxis: {
      title: {
        text: 'Number of Visits',
        style: {
          fontSize: '14px',
          fontWeight: 400
        }
      },
      labels: {
        formatter: value => Math.round(value).toString()
      }
    },
    tooltip: {
      shared: true,
      x: {
        format: 'dd MMM yyyy'
      },
      y: {
        formatter: value => Math.round(value).toString()
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      show: modelingType.includes(',')
    }
  }

  return { series, options }
}
export const formatTimeToFirstByteData = (
  datewiseData: DatewiseData[],
  period: string,
  continents: ContinentsData[]
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let categories = [] as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const series = [] as any
  // console.log('continents', continents)
  // Predefined color map for continents
  if (continents?.length > 0) {
    const uniqueDates = new Set<string>() // Store unique dates

    const formatDate = (dateString: string) => {
      if (period === 'last 7 days') {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', { weekday: 'short' }) // Example: "Mon"
      }
      const date = new Date(dateString)
      const day = date.getDate()
      const month = date.toLocaleDateString('en-US', { month: 'short' })
      const year = date.getFullYear()
      const formattedDate = `${day} ${month}, ${year}`
      return formattedDate
    }
    // Extract unique dates across all continents
    continents?.forEach((continentObj: ContinentsData) => {
      Object.values(continentObj).forEach(({ 'datewise-data': data }) => {
        data.forEach((entry: DatewiseData) =>
          uniqueDates.add(formatDate(entry.date))
        ) // Add formatted date
      })
    })

    categories = Array.from(uniqueDates) // Sorted unique dates for x-axis
    let counter = 0
    continents?.forEach((continentObj: ContinentsData) => {
      Object.entries(continentObj).forEach(([continentName, data]) => {
        const datewiseData = data['datewise-data']
        counter++
        const regionStyle = REGION_STYLES[
          continentName as keyof typeof REGION_STYLES
        ] ||
          RANDOM_COLOR_PALETTE[counter] || {
            lineColor: '#000000',
            gradientFrom: 'rgba(0,0,0,0.45)',
            gradientTo: 'rgba(0,0,0,0.05)',
            dashArray: 0
          }
        series.push({
          name: `Average Load Time ${continentName}`,
          data: categories.map((date: string) => {
            const match = datewiseData.find(
              (entry: DatewiseData) => formatDate(entry.date) === date
            )
            return match ? match.avg_time_network : 0 // Ensure all dates have values
          }),
          color: regionStyle.lineColor
        })
      })
    })
  } else {
    categories = datewiseData.map(data => {
      if (period === 'last 7 days') {
        const date = new Date(data.date)
        return date.toLocaleDateString('en-US', { weekday: 'short' })
      }
      const date = new Date(data.date)
      const day = date.getDate()
      const month = date.toLocaleDateString('en-US', { month: 'short' })
      const year = date.getFullYear()
      const formattedDate = `${day} ${month}, ${year}`
      return formattedDate
    })

    const networkTimes = datewiseData.map(item => item.avg_time_network)

    series.push({
      name: 'Time to First Byte',
      data: networkTimes
    })
  }

  const options: ApexOptions = {
    chart: {
      type: 'area',
      toolbar: {
        show: false
      }
      // animations: {
      //   enabled: false // Disable animations for sharper lines
      // }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'straight',
      width: 2,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      colors:
        continents?.length > 0
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            series.map((item: any) => item.color)
          : ['#7B6EF6'] // More vibrant purple color
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [0, 100],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        colorStops:
          continents.length > 0
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              series.flatMap((item: any) => [
                {
                  offset: 0,
                  color:
                    REGION_STYLES[item?.name as keyof typeof REGION_STYLES]
                      ?.gradientFrom || 'rgba(0,0,0,0.45)',
                  opacity: 0.45
                },
                {
                  offset: 100,
                  color:
                    REGION_STYLES[item?.name as keyof typeof REGION_STYLES]
                      ?.gradientTo || 'rgba(0,0,0,0.05)',
                  opacity: 0.05
                }
              ])
            : [
                {
                  offset: 0,
                  color: '#7B6EF6',
                  opacity: 0.45
                },
                {
                  offset: 100,
                  color: '#7B6EF6',
                  opacity: 0.05
                }
              ]
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    colors:
      continents?.length > 0
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          series.map((item: any) => item?.color)
        : ['#7B6EF6'],
    xaxis: {
      categories: categories,
      labels: {
        style: {
          colors: '#64748B',
          fontSize: '12px'
        }
      }
      // axisBorder: {
      //   show: false
      // },
      // axisTicks: {
      //   show: false
      // }
    },
    yaxis: {
      // min: 0,
      // max: Math.ceil(Math.max(...networkTimes) + 0.5),
      // tickAmount: 5,
      title: {
        text: 'Average load time',
        style: {
          fontSize: '14px',
          fontWeight: 400
        }
      },
      labels: {
        formatter: (value: number) => `${value.toFixed(1)}s`,
        style: {
          colors: '#64748B',
          fontSize: '12px'
        }
      }
    },
    tooltip: {
      // theme: 'light',
      // x: {
      //   show: true,
      //   formatter: (val: number, opts?: any) => {
      //     return categories[val - 1]
      //   }
      // },
      y: {
        formatter: (value: number) => `${value.toFixed(2)}s`
        //     formatter: (value: number) => `Average load time ${value.toFixed(1)}s`
      }
      //   marker: {
      //     show: true
      //   }
      // },
      // markers: {
      //   size: 0,
      //   hover: {
      //     size: 5,
      //     sizeOffset: 0
      //   }
    },
    legend: {
      show: continents?.length > 0 ? true : false,
      formatter: seriesName => seriesName.replace('Avg Page Load - ', ''),
      onItemClick: {
        toggleDataSeries: false
      },
      position: 'bottom', // Places the legend below
      horizontalAlign: 'left', // Aligns legend to bottom-left
      fontSize: '12px',
      labels: {
        colors: '#64748B'
      }
    }
  }

  return { series, options }
}

// Define the type for filter values
export interface DefaultFilterValues {
  kpi: string
  forecast_days: number
  date_range: string
  region: string
  device_type: string
  traffic_source: string
  location_type: string
  locations: string[]
  user_segment: string
  modeling_data_type: string
}

// Define the default values
export const defaultAnalyticsFilters: DefaultFilterValues = {
  kpi: 'nb_visits',
  forecast_days: 30,
  date_range: 'last30',
  region: '',
  device_type: 'desktop',
  traffic_source: 'direct',
  location_type: '',
  locations: [],
  user_segment: '',
  modeling_data_type: 'predictive, historical'
} as const

// Generic interface for date-wise data
export interface DateWiseDataBase {
  day?: string
  date?: string
}

// Function type with generic constraint
export function formatDateWiseData<T extends DateWiseDataBase>(
  datewiseData: T[] | undefined,
  period: string
): string[] {
  if (!datewiseData || datewiseData.length === 0) return []
  return datewiseData.map(data => {
    if (period === 'last 7 days') {
      const date = new Date(data.date || '')
      return date.toLocaleDateString('en-US', { weekday: 'short' })
    }
    const date = new Date(data.date || '')
    const day = date.getDate()
    const month = date.toLocaleDateString('en-US', { month: 'short' })
    const year = date.getFullYear()
    return `${day} ${month}, ${year}`
  })
}